/**
 * The vehicle check and defect report on the phone (docs/mobile-design.md Phase 3, chunk 2), shared by mobile web and
 * React Native: which vehicle a check starts on, walkaround progress and what still blocks sending, the defect report's
 * choices and blocker, and the self-employed driver's three tabs. Pure TS.
 */
import { WALKAROUND_ITEMS } from '../fleet/logic';
import type { FleetReportKind, Journey, Vehicle } from '../fleet/types';
import { groupMyRuns } from './runs';

// ------------------------------------------------------------------------------------------------ vehicle
type RunForVehicle = Pick<Journey, 'id' | 'ref' | 'status' | 'planned_start' | 'planned_end' | 'vehicle_id'>;
export type CheckVehicleSource = 'run' | 'next_run' | 'only' | null;

/**
 * The vehicle a check or report starts on: the given run's, else the run in progress or today's next one, else the
 * next upcoming run's, else the only vehicle there is. Off-road vehicles can still be reported (not checked).
 */
export function checkVehicle(o: { runs: RunForVehicle[]; vehicles: Pick<Vehicle, 'id' | 'status'>[]; journeyId?: string | null; now?: number }): { vehicle_id: string | null; journey_id: string | null; source: CheckVehicleSource } {
  const usable = new Set(o.vehicles.filter(v => v.status !== 'retired').map(v => v.id));
  const given = o.journeyId ? o.runs.find(r => r.id === o.journeyId) : null;
  if (given?.vehicle_id && usable.has(given.vehicle_id)) return { vehicle_id: given.vehicle_id, journey_id: given.id, source: 'run' };
  const g = groupMyRuns(o.runs, o.now);
  const today = g.today.find(r => r.status !== 'completed' && r.vehicle_id && usable.has(r.vehicle_id));
  if (today) return { vehicle_id: today.vehicle_id, journey_id: today.id, source: 'run' };
  const next = g.later.find(r => r.vehicle_id && usable.has(r.vehicle_id));
  if (next) return { vehicle_id: next.vehicle_id, journey_id: null, source: 'next_run' };
  const only = o.vehicles.filter(v => v.status !== 'retired');
  if (only.length === 1) return { vehicle_id: only[0]!.id, journey_id: null, source: 'only' };
  return { vehicle_id: null, journey_id: null, source: null };
}
export const CHECK_VEHICLE_HINT: Record<Exclude<CheckVehicleSource, null>, string> = { run: 'From your run today', next_run: 'From your next run', only: 'Your vehicle' };

// ------------------------------------------------------------------------------------------------ walkaround
export interface WalkItemState { ok: boolean | null; note: string }
export type WalkState = Record<string, WalkItemState>;
export const emptyWalkaround = (): WalkState => Object.fromEntries(WALKAROUND_ITEMS.map(i => [i.key, { ok: null, note: '' }]));

export function walkaroundProgress(items: WalkState): { checked: number; faults: number; total: number; label: string } {
  const v = WALKAROUND_ITEMS.map(i => items[i.key]);
  const checked = v.filter(x => x && x.ok !== null).length;
  const faults = v.filter(x => x?.ok === false).length;
  return { checked, faults, total: WALKAROUND_ITEMS.length, label: `${checked} of ${WALKAROUND_ITEMS.length} checked${faults ? ` · ${faults} fault${faults === 1 ? '' : 's'}` : ''}` };
}

/** What stops the check from being sent (mirrors the server: every item answered, each fault described). */
export function walkaroundBlocker(o: { vehicle_id: string | null | undefined; items: WalkState; online?: boolean; uploading?: boolean }): string | null {
  if (!o.vehicle_id) return 'Pick the vehicle';
  const p = walkaroundProgress(o.items);
  if (p.checked < p.total) return `Check ${p.total - p.checked} more`;
  const vague = WALKAROUND_ITEMS.find(i => o.items[i.key]?.ok === false && !o.items[i.key]!.note.trim());
  if (vague) return `Say what's wrong with ${vague.label.toLowerCase()}`;
  if (o.uploading) return 'Waiting for photos to upload';
  if (o.online === false) return 'You’re offline - send it when you have signal';
  return null;
}

export const walkaroundSubmitLabel = (faults: number, online = true) =>
  `${online ? 'Send' : 'Save'} check · ${faults ? `${faults} fault${faults === 1 ? '' : 's'}` : 'all OK'}`;

/** The result line after sending. */
export function walkaroundResult(r: { passed: boolean; incident_ref: number | null }, reg: string): { tone: 'ok' | 'bad'; title: string; body: string } {
  if (r.passed) return { tone: 'ok', title: 'Safe to drive', body: `${reg} passed its walkaround check.` };
  return { tone: 'bad', title: `${reg} is off road`, body: `Don't drive it. The fleet team has been told${r.incident_ref ? ` (issue #${r.incident_ref})` : ''} and will clear it once it's fixed.` };
}

// ------------------------------------------------------------------------------------------------ no signal
/**
 * A check or report saved on the phone with no signal (the run outbox sends it, once, when there is signal). The issue
 * number only exists once the server has it, so the result screen says what happens next instead.
 */
export function queuedResult(kind: 'check' | 'report', o: { faults?: number; off_road?: boolean; reg: string }): { tone: 'ok' | 'bad'; title: string; body: string } {
  if (kind === 'check' && !o.faults) return { tone: 'ok', title: 'Safe to drive', body: `${o.reg} passed. Your check is saved on your phone and sends by itself when you have signal.` };
  const bad = kind === 'check' || Boolean(o.off_road);
  return { tone: bad ? 'bad' : 'ok', title: 'Saved on your phone', body: `${bad ? `Don't drive ${o.reg}. ` : ''}Your ${kind} sends by itself when you have signal, and the issue number shows then.` };
}

/** The line in the waiting-to-send list: "Vehicle check · MX22 TOV · 1 fault", "Defect report · MX22 TOV". */
export function fleetOpLabel(kind: 'check' | 'report', reg: string, o: { faults?: number; reportKind?: FleetReportKind } = {}): string {
  if (kind === 'check') return `Vehicle check · ${reg}${o.faults ? ` · ${o.faults} fault${o.faults === 1 ? '' : 's'}` : ''}`;
  const k = DEFECT_KINDS.find(x => x.id === o.reportKind)?.label ?? 'Defect';
  return `${k} report · ${reg}`;
}

/** The toast once a saved check or report has gone: "Vehicle check sent · issue #41 raised". */
export function fleetSentToast(kind: 'check' | 'report', r: { passed?: boolean; incident_ref?: number | null } | null | undefined): string {
  const what = kind === 'check' ? 'Vehicle check sent' : 'Report sent';
  if (r?.incident_ref) return `${what} · issue #${r.incident_ref} raised`;
  return kind === 'check' && r?.passed ? `${what} · all OK` : what;
}

// ------------------------------------------------------------------------------------------------ defect report
export const DEFECT_KINDS: { id: FleetReportKind; label: string; hint: string }[] = [
  { id: 'defect', label: 'Defect', hint: 'Something is broken or worn' },
  { id: 'damage', label: 'Damage', hint: 'A scrape, dent or crack' },
  { id: 'accident', label: 'Accident', hint: 'A collision or a near miss on the road' },
];
export type DefectSeverity = 'low' | 'medium' | 'high';
export const DEFECT_SEVERITIES: { id: DefectSeverity; label: string }[] = [
  { id: 'low', label: 'Minor - can wait' }, { id: 'medium', label: 'Needs fixing soon' }, { id: 'high', label: "Unsafe - don't drive" },
];
export const DEFECT_PHOTO_MAX = 4;

/** Accidents and unsafe defects always take the vehicle off road; otherwise it's the driver's call. */
export const defectForcesOffRoad = (kind: FleetReportKind, severity: DefectSeverity) => kind === 'accident' || severity === 'high';
export const defectOffRoad = (kind: FleetReportKind, severity: DefectSeverity, chosen: boolean) => defectForcesOffRoad(kind, severity) || chosen;

export function defectBlocker(o: { vehicle_id: string | null | undefined; description: string; online?: boolean; uploading?: boolean }): string | null {
  if (!o.vehicle_id) return 'Pick the vehicle';
  if (!o.description.trim()) return 'Say what happened';
  if (o.uploading) return 'Waiting for photos to upload';
  if (o.online === false) return 'You’re offline - send it when you have signal';
  return null;
}

/** "Issue #42 raised · the fleet team has it · vehicle off road". */
export const defectSentLine = (r: { incident_ref: number | null; vehicle_off_road: boolean }) =>
  `${r.incident_ref ? `Issue #${r.incident_ref} raised` : 'Reported'} · the fleet team has it${r.vehicle_off_road ? ' · vehicle off road' : ''}`;

/** A report can carry a map pin from the phone's location only during a run in progress (driver location is per run). */
export const canPinReportLocation = (run: Pick<Journey, 'status'> | null | undefined) => run?.status === 'in_progress';

// ------------------------------------------------------------------------------------------------ self-employed shell
export type ExternalDriverTab = 'runs' | 'check' | 'report';
/** Self-employed drivers keep three tabs: their runs, the vehicle check and reporting a problem. */
export const EXTERNAL_DRIVER_TABS: { id: ExternalDriverTab; label: string; to: string }[] = [
  { id: 'runs', label: 'Runs', to: '/fleet/my-runs' }, { id: 'check', label: 'Check', to: '/fleet/check' }, { id: 'report', label: 'Report', to: '/fleet/report' },
];
export function externalDriverTab(path: string): ExternalDriverTab {
  if (path.startsWith('/fleet/check')) return 'check';
  if (path.startsWith('/fleet/report')) return 'report';
  return 'runs';
}
